import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { styles } from "../../styles/pages/JobTitle/JobTitleEditStyles";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import Loader from "../../components/common/Loader";

const JobTitleEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [job_title, setJobTitleName] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  // Fetch job title data
  useEffect(() => {
    // const job_title_id = location.state?.job_title_id;
    const token = sessionStorage.getItem("access_token");

    if (!token || !id) {
      navigate("/signin");
      setLoading(false);
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/jobtitle/edit`,
        { job_title_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((response) => {
        const data = response.data.data[0];
        if (data) {
          setJobTitleName(data.job_title || "");
        }
      })
      .catch((error) => {
        console.error("Error fetching job title data:", error);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    if (!job_title.trim()) {
      setErrors({ job_title: "The job title field is required." });
      setSaving(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");
    // const job_title_id = location.state?.job_title_id;

    if (!token || !id) {
      navigate("/signin");
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/jobtitle/edit`,
        { job_title_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((jobtitleResponse) => {
        const currentStatus = jobtitleResponse.data?.data[0]?.status || "0";
        const formData = new FormData();
        formData.append("job_title_id", id);
        formData.append("job_title", job_title);
        formData.append("status", currentStatus);

        return axios.post(
          `${process.env.REACT_APP_API_URL}/api/jobtitle/update`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      })
      .then((response) => {
        if (response.data.status === 200) {
          setSuccessModalOpen(true); // Show success modal
          setTimeout(() => {
            setSuccessModalOpen(false);
            navigate("/job-title-list"); // Redirect after modal
          }, 2000); // Delay for better UX
        }
      })
      .catch((error) => {
        console.error("API Error:", error.response?.data || error.message);
        if (error.response?.status === 422) {
          setErrors(error.response.data.errors || {});
        }
      })
      .finally(() => setSaving(false));
  };

  // Handle input change and clear error state
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setJobTitleName(value);
    if (!value.trim()) {
      setErrors({ job_title: "The job title field is required." });
    } else {
      setErrors({ job_title: undefined });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className="flex items-center space-x-4">
          <h2 className={styles.title}>Edit Job Title</h2>
        </div>
      </div>
      {loading && (
        <div className="mt-5">
          <Loader />
        </div>
      )}
      {!loading && (
        <div className={styles.formContainer}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputSection}>
              <div>
                <label className={styles.label}>
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={job_title}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.job_title
                      ? styles.inputError
                      : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                  }`}
                />
                {errors.job_title && (
                  <span className={styles.errorText}>{errors.job_title}</span>
                )}
              </div>
            </div>

            <div className={styles.buttonSection}>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => navigate("/job-title-list")}
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Success Modal */}
          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
          >
            {/* Close Button */}
            <button
              onClick={() => setSuccessModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            ></button>

            {/* Success Icon */}
            <div className="flex justify-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>

            {/* Modal Heading */}
            <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
              Job Title Updated Successfully
            </h4>

            {/* Modal Text */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              The Job Title has been updated in the system successfully.
            </p>

            {/* Button */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setSuccessModalOpen(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default JobTitleEdit;
